/**
 * Formatage francais.
 *
 * Les dates sont composees a la main plutot que via `Intl.DateTimeFormat` :
 * le rendu reste identique quelle que soit la version d'ICU embarquee, ce qui
 * rend les tests deterministes.
 */

const ESPACE_FINE = ' ';
const ESPACE_INSECABLE = ' ';

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'] as const;
const JOURS_ABREGES = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'] as const;

/** Analyse une date ISO ; renvoie `null` si la valeur est absente ou invalide. */
export function versDate(valeur: string | Date | null | undefined): Date | null {
  if (!valeur) return null;
  const date = valeur instanceof Date ? valeur : new Date(valeur);
  return Number.isNaN(date.getTime()) ? null : date;
}

function deuxChiffres(valeur: number): string {
  return valeur < 10 ? `0${valeur}` : String(valeur);
}

/** "12/03/2024" */
export function formaterDate(valeur: string | Date | null | undefined, defaut = '—'): string {
  const date = versDate(valeur);
  if (!date) return defaut;
  return `${deuxChiffres(date.getDate())}/${deuxChiffres(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** "12/03/2024 à 14:05" */
export function formaterDateHeure(valeur: string | Date | null | undefined, defaut = '—'): string {
  const date = versDate(valeur);
  if (!date) return defaut;
  return `${formaterDate(date)} à ${deuxChiffres(date.getHours())}:${deuxChiffres(date.getMinutes())}`;
}

/** "09:05" (valeur attendue par un `<input type="time">`). */
export function formaterHeure(valeur: string | Date | null | undefined, defaut = '—'): string {
  const date = versDate(valeur);
  return date ? `${deuxChiffres(date.getHours())}:${deuxChiffres(date.getMinutes())}` : defaut;
}

/** Valeur `yyyy-mm-dd` attendue par un `<input type="date">`. */
export function versValeurInputDate(valeur: string | Date | null | undefined): string {
  const date = versDate(valeur);
  if (!date) return '';
  return `${date.getFullYear()}-${deuxChiffres(date.getMonth() + 1)}-${deuxChiffres(date.getDate())}`;
}

/** Valeur `yyyy-mm-ddThh:mm` attendue par un `<input type="datetime-local">`. */
export function versValeurInputDateHeure(valeur: string | Date | null | undefined): string {
  const date = versDate(valeur);
  if (!date) return '';
  return `${versValeurInputDate(date)}T${deuxChiffres(date.getHours())}:${deuxChiffres(date.getMinutes())}`;
}

/* -------------------------------------------------------------------------- */
/*                                   Nombres                                   */
/* -------------------------------------------------------------------------- */

/** "12 345" (espace fine insecable comme separateur de milliers). */
export function formaterNombre(valeur: number | null | undefined, defaut = '—'): string {
  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) return defaut;
  const negatif = valeur < 0;
  const entier = Math.round(Math.abs(valeur)).toString();
  const groupe = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ESPACE_FINE);
  return negatif ? `-${groupe}` : groupe;
}

/** "12,5 %" */
export function formaterPourcentage(
  valeur: number | null | undefined,
  decimales = 1,
  defaut = '—',
): string {
  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) return defaut;
  return `${valeur.toFixed(decimales).replace('.', ',')}${ESPACE_INSECABLE}%`;
}

/** Note Google sur 5 : "4,3". */
export function formaterNote(valeur: number | null | undefined, defaut = '—'): string {
  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) return defaut;
  return valeur.toFixed(1).replace('.', ',');
}

/** Duree d'appel : "45 s", "12 min", "1 h 05". */
export function formaterDuree(secondes: number | null | undefined, defaut = '—'): string {
  if (secondes === null || secondes === undefined || Number.isNaN(secondes)) return defaut;
  if (secondes < 60) return `${Math.round(secondes)}${ESPACE_INSECABLE}s`;
  const minutes = Math.floor(secondes / 60);
  if (minutes < 60) return `${minutes}${ESPACE_INSECABLE}min`;
  const heures = Math.floor(minutes / 60);
  return `${heures}${ESPACE_INSECABLE}h${ESPACE_INSECABLE}${deuxChiffres(minutes % 60)}`;
}

/* -------------------------------------------------------------------------- */
/*                                 Anciennete                                  */
/* -------------------------------------------------------------------------- */

const JOUR_MS = 86_400_000;

/** Nombre d'annees revolues entre deux dates, `null` si la date est absente. */
export function ancienneteEnAnnees(
  valeur: string | Date | null | undefined,
  maintenant: Date = new Date(),
): number | null {
  const date = versDate(valeur);
  if (!date) return null;
  let annees = maintenant.getFullYear() - date.getFullYear();
  const moisEcoules = maintenant.getMonth() - date.getMonth();
  if (moisEcoules < 0 || (moisEcoules === 0 && maintenant.getDate() < date.getDate())) annees -= 1;
  return annees;
}

/**
 * Anciennete lisible d'un etablissement : "créé il y a 7 ans".
 * Sous un an on descend au mois, sous un mois au jour.
 */
export function formaterAnciennete(
  valeur: string | Date | null | undefined,
  maintenant: Date = new Date(),
  defaut = 'Date de création inconnue',
): string {
  const date = versDate(valeur);
  if (!date) return defaut;

  const annees = ancienneteEnAnnees(date, maintenant) ?? 0;
  if (annees >= 2) return `créé il y a ${annees} ans`;
  if (annees === 1) return 'créé il y a 1 an';

  const jours = Math.floor((maintenant.getTime() - date.getTime()) / JOUR_MS);
  if (jours < 0) return `création prévue le ${formaterDate(date)}`;
  if (jours === 0) return "créé aujourd'hui";
  if (jours === 1) return 'créé hier';
  if (jours < 31) return `créé il y a ${jours} jours`;

  const mois = Math.max(1, Math.floor(jours / 30.44));
  return mois === 1 ? 'créé il y a 1 mois' : `créé il y a ${mois} mois`;
}

/** Ecart relatif signe : "dans 3 jours", "il y a 2 jours", "aujourd'hui". */
export function formaterEcartJours(
  valeur: string | Date | null | undefined,
  maintenant: Date = new Date(),
  defaut = '—',
): string {
  const date = versDate(valeur);
  if (!date) return defaut;
  const debutCible = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const debutJour = new Date(
    maintenant.getFullYear(),
    maintenant.getMonth(),
    maintenant.getDate(),
  ).getTime();
  const jours = Math.round((debutCible - debutJour) / JOUR_MS);

  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return 'demain';
  if (jours === -1) return 'hier';
  if (jours > 0) return `dans ${jours} jours`;
  return `il y a ${Math.abs(jours)} jours`;
}

/** Ecart en jours calendaires (negatif : passe) ; l'heure ne compte pas. */
export function ecartEnJours(valeur: Date, maintenant: Date): number {
  const debut = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  // L'arrondi absorbe les jours de 23 ou 25 heures des changements d'heure.
  return Math.round((debut(valeur) - debut(maintenant)) / JOUR_MS);
}

/**
 * Jour civil `AAAA-MM-JJ` en instant de minuit UTC, `null` s'il est illisible : ses jour de la semaine et ecarts se
 * lisent en UTC, sans le fuseau du navigateur.
 */
function jourCivilEnUtc(jour: string): Date | null {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (!morceaux) return null;
  const date = new Date(Date.UTC(Number(morceaux[1]), Number(morceaux[2]) - 1, Number(morceaux[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * « 15/01/2026 » pour un jour civil `AAAA-MM-JJ` venu de l'API, sans conversion de fuseau : `formaterDate` le lirait
 * comme minuit UTC, donc la veille a l'ouest de Greenwich.
 */
export function formaterDateCivile(jour: string, defaut = '—'): string {
  const date = jourCivilEnUtc(jour);
  return date ? `${deuxChiffres(date.getUTCDate())}/${deuxChiffres(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}` : defaut;
}

/** « mardi 22 septembre 2026 » pour un jour civil `AAAA-MM-JJ` (jour de Paris), sans conversion de fuseau. */
export function formaterJourCivilComplet(jour: string, defaut = '—'): string {
  const date = jourCivilEnUtc(jour);
  if (!date) return defaut;
  const quantieme = date.getUTCDate();
  return `${JOURS[date.getUTCDay()] ?? ''} ${quantieme === 1 ? '1er' : quantieme} ${MOIS[date.getUTCMonth()] ?? ''} ${date.getUTCFullYear()}`;
}

/**
 * Titre d'un jour de frise, jours civils de Paris : « Aujourd'hui », « Hier », « Demain » par rapport a
 * `aujourdhui`, sinon la date en toutes lettres.
 */
export function libelleJourRelatif(jour: string, aujourdhui: string): string {
  const date = jourCivilEnUtc(jour);
  const reference = jourCivilEnUtc(aujourdhui);
  if (!date || !reference) return '—';
  const ecart = Math.round((date.getTime() - reference.getTime()) / JOUR_MS);
  if (ecart === 0) return "Aujourd'hui";
  if (ecart === -1) return 'Hier';
  if (ecart === 1) return 'Demain';
  return formaterJourCivilComplet(jour);
}

/**
 * Anciennete courte d'un evenement recent : « à l'instant », « il y a 2 min »,
 * « il y a 3 h », « hier », « il y a 4 jours », puis la date. Une date un peu
 * future (horloge du serveur en avance) vaut « à l'instant ».
 */
export function formaterIlYa(valeur: string | Date | null | undefined, maintenant: Date = new Date(), defaut = '—'): string {
  const date = versDate(valeur);
  if (!date) return defaut;
  const minutes = Math.floor((maintenant.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes}${ESPACE_INSECABLE}min`;
  const heures = Math.floor(minutes / 60);
  if (heures < 24) return `il y a ${heures}${ESPACE_INSECABLE}h`;
  const jours = -ecartEnJours(date, maintenant);
  if (jours <= 1) return 'hier';
  if (jours < 7) return `il y a ${jours} jours`;
  return `le ${formaterDate(date)}`;
}

/** Cout estime en dollars : « 0,04 $ » ; un montant qui s'arrondirait a zero est « moins de 0,01 $ ». */
export function formaterCoutUsd(valeur: number | null | undefined, defaut = 'non estimé'): string {
  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) return defaut;
  if (valeur > 0 && valeur < 0.005) return `moins de 0,01${ESPACE_INSECABLE}$`;
  const [entier = '0', centimes = '00'] = valeur.toFixed(2).split('.');
  return `${formaterNombre(Number(entier))},${centimes}${ESPACE_INSECABLE}$`;
}

/** Mois `AAAA-MM` d'une date, heure locale. */
export function moisDe(date: Date): string {
  return `${date.getFullYear()}-${deuxChiffres(date.getMonth() + 1)}`;
}

/** Le mois de `maintenant` puis les precedents, du plus recent au plus ancien. */
export function moisPrecedents(maintenant: Date, nombre: number): string[] {
  return Array.from({ length: nombre }, (_, rang) => moisDe(new Date(maintenant.getFullYear(), maintenant.getMonth() - rang, 1)));
}

/** « septembre 2026 » pour `2026-09`. */
export function formaterMois(mois: string, defaut = '—'): string {
  const morceaux = /^(\d{4})-(\d{2})$/.exec(mois);
  const nom = morceaux ? MOIS[Number(morceaux[2]) - 1] : undefined;
  return morceaux && nom ? `${nom} ${morceaux[1]}` : defaut;
}

/**
 * Jour civil `AAAA-MM-JJ` en « jeu. 10/09 ». Lu champ par champ : `new Date('2026-09-10')`
 * serait minuit UTC, donc la veille a l'ouest de Greenwich.
 */
export function formaterJourCivil(jour: string, defaut = '—'): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour);
  if (!morceaux) return defaut;
  const date = new Date(Number(morceaux[1]), Number(morceaux[2]) - 1, Number(morceaux[3]));
  return `${JOURS_ABREGES[date.getDay()] ?? ''} ${morceaux[3]}/${morceaux[2]}`;
}

/**
 * « Mercredi 30 septembre » pour un jour civil `AAAA-MM-JJ` (le jour de Paris
 * servi par l'API ou `jourParis`) : aucune conversion de fuseau n'intervient.
 */
export function formaterJourCivilLong(jour: string): string {
  const [annee = Number.NaN, mois = Number.NaN, quantieme = Number.NaN] = jour.split('-').map(Number);
  const date = new Date(Date.UTC(annee, mois - 1, quantieme));
  if (Number.isNaN(date.getTime())) return jour;
  const texte = `${JOURS[date.getUTCDay()] ?? ''} ${quantieme === 1 ? '1er' : quantieme} ${MOIS[mois - 1] ?? ''}`;
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/* -------------------------------------------------------------------------- */
/*                                  Telephone                                  */
/* -------------------------------------------------------------------------- */

/**
 * Met en forme un numero francais par paires : "06 12 34 56 78".
 * Les numeros etrangers ou non reconnus sont renvoyes tels quels.
 */
export function formaterTelephone(valeur: string | null | undefined, defaut = '—'): string {
  if (!valeur) return defaut;
  const brut = valeur.replace(/[^\d+]/g, '');

  let national = '';
  if (/^0\d{9}$/.test(brut)) national = brut;
  else if (/^\+33\d{9}$/.test(brut)) national = `0${brut.slice(3)}`;
  else if (/^0033\d{9}$/.test(brut)) national = `0${brut.slice(4)}`;

  if (!national) return valeur.trim();
  return (national.match(/\d{2}/g) ?? []).join(ESPACE_INSECABLE);
}

/** Numero utilisable dans un lien `tel:`. */
export function lienTelephone(valeur: string | null | undefined): string | null {
  if (!valeur) return null;
  const brut = valeur.replace(/[^\d+]/g, '');
  return brut ? `tel:${brut}` : null;
}

/** Ajoute le schema https a une URL saisie sans, pour un lien cliquable sur. */
export function normaliserUrl(valeur: string | null | undefined): string | null {
  if (!valeur) return null;
  const propre = valeur.trim();
  if (!propre) return null;
  if (/^https?:\/\//i.test(propre)) return propre;
  return `https://${propre}`;
}

/** Domaine seul, pour un affichage compact du site web. */
export function domaineLisible(valeur: string | null | undefined): string {
  const url = normaliserUrl(valeur);
  if (!url) return '—';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return valeur ?? '—';
  }
}

/** "812 345 676 00017" : le SIREN par groupes de trois, puis le NIC, sans retour a la ligne. */
export function formaterSiret(valeur: string | null | undefined, defaut = '—'): string {
  if (!valeur) return defaut;
  const chiffres = valeur.replace(/\s+/g, '');
  if (!/^\d{14}$/.test(chiffres)) return valeur.trim();
  return [chiffres.slice(0, 3), chiffres.slice(3, 6), chiffres.slice(6, 9), chiffres.slice(9)].join(ESPACE_INSECABLE);
}

/** "900 000 011" : le SIREN groupe comme en tete du SIRET, sans retour a la ligne. */
export function formaterSiren(valeur: string | null | undefined, defaut = '—'): string {
  if (!valeur) return defaut;
  const chiffres = valeur.replace(/\s+/g, '');
  if (!/^\d{9}$/.test(chiffres)) return valeur.trim();
  return [chiffres.slice(0, 3), chiffres.slice(3, 6), chiffres.slice(6)].join(ESPACE_INSECABLE);
}

/** "33000 Bordeaux" */
export function formaterCommune(codePostal: string | null | undefined, ville: string | null | undefined, defaut = 'Commune inconnue'): string {
  return [codePostal, ville].filter(Boolean).join(' ') || defaut;
}

/** "1 lead" / "24 leads" */
export function pluriel(nb: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${formaterNombre(nb)} ${Math.abs(nb) > 1 ? plurielForme : singulier}`;
}

/** Longueur des notes d'un echange que la fiche d'appel montre, et que la session d'appels garde dans l'onglet. */
const LONGUEUR_EXTRAIT = 140;

/** Notes rognees, coupees par des points de suspension au-dela de la longueur affichee. */
export function extraitDeNotes(notes: string): string {
  const propres = notes.trim();
  return propres.length > LONGUEUR_EXTRAIT ? `${propres.slice(0, LONGUEUR_EXTRAIT - 1)}…` : propres;
}

/** « de Camille », « d'Alex » : elision devant une voyelle ou un h. */
export function avecDe(nom: string): string {
  return /^[aeiouyàâäéèêëîïôöùûüœh]/iu.test(nom) ? `d'${nom}` : `de ${nom}`;
}

/** Initiales pour l'avatar de l'en-tete. */
export function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');
}
